// ══════════════════════════════════════════════════════════════
// COMPASS – Process Effects
// ══════════════════════════════════════════════════════════════
import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { concat, EMPTY, forkJoin, from, Observable, of, throwError, timer } from 'rxjs';
import { catchError, exhaustMap, filter, groupBy, map, mergeMap, pairwise, switchMap, take, takeUntil, takeWhile, withLatestFrom } from 'rxjs/operators';
import { ProcessActions } from './process.actions';
import { FORM_CONFLICT_CODES, isFormConflict } from './process-form-conflict';
import { ToastActions } from '../toast/toast.actions';
import { ApiConfiguration } from '../../api/api-configuration';
import { getAll2 } from '../../api/fn/kyc-process/get-all-2';
import { getById3 } from '../../api/fn/kyc-process/get-by-id-3';
import { getMyProcesses } from '../../api/fn/kyc-process/get-my-processes';
import { getIndividualForm } from '../../api/fn/kyc-form/get-individual-form';
import { updateIndividualForm } from '../../api/fn/kyc-form/update-individual-form';
import { getCompanyForm } from '../../api/fn/kyc-form/get-company-form';
import { updateCompanyForm } from '../../api/fn/kyc-form/update-company-form';
import { getAllWithItems } from '../../api/fn/kyc-change-request/get-all-with-items';
import { createWithItems } from '../../api/fn/kyc-change-request/create-with-items';
import { close } from '../../api/fn/kyc-change-request/close';
import { reopen } from '../../api/fn/kyc-change-request/reopen';
import { transitionStatus } from '../../api/fn/kyc-process/transition-status';
import { reopenProcess } from '../../api/fn/kyc-process/reopen-process';
import { clientReopen } from '../../api/fn/kyc-process/client-reopen';
import { listFormVersions } from '../../api/fn/kyc-process/list-form-versions';
import { getFormVersion } from '../../api/fn/kyc-process/get-form-version';
import { submit } from '../../api/fn/kyc-process/submit';
import { createWithoutClient } from '../../api/fn/kyc-process/create-without-client';
import { delete3 } from '../../api/fn/kyc-process/delete-3';
import { withdrawCustomerInvitation } from '../../api/fn/invitations/withdraw-customer-invitation';
import { KycFormSnapshotSummaryDto } from '../../api/models/kyc-form-snapshot-summary-dto';
import { KycFormSnapshotDto } from '../../api/models/kyc-form-snapshot-dto';
import { getApprovalState } from '../../api/fn/kyc-process-approval/get-approval-state';
import { getApprovals } from '../../api/fn/kyc-process-approval/get-approvals';
import { decide } from '../../api/fn/kyc-process-approval/decide';
import { getTimeline } from '../../api/fn/kyc-process/get-timeline';
import { getAllByProcessId } from '../../api/fn/kyc-process-participant/get-all-by-process-id';
import { getState } from '../../api/fn/client-risk-matrix/get-state';
import { assess } from '../../api/fn/client-risk-matrix/assess';
import { getMeasures as getRiskMeasures } from '../../api/fn/client-risk-matrix/get-measures';
import { saveMeasures as saveRiskMeasures } from '../../api/fn/client-risk-matrix/save-measures';
import { getSourceOfFundsObservations } from '../../api/fn/kyc-source-of-funds-observations/get-source-of-funds-observations';
import { saveSourceOfFundsObservations } from '../../api/fn/kyc-source-of-funds-observations/save-source-of-funds-observations';
import { getGeographicRiskAnalysis } from '../../api/fn/kyc-geographic-risk-analysis/get-geographic-risk-analysis';
import { saveGeographicRiskAnalysis } from '../../api/fn/kyc-geographic-risk-analysis/save-geographic-risk-analysis';
import { getFinalObservations } from '../../api/fn/kyc-final-observations/get-final-observations';
import { saveFinalObservations } from '../../api/fn/kyc-final-observations/save-final-observations';
import { StrictHttpResponse } from '../../api/strict-http-response';
import { KycSourceOfFundsObservationsDto } from '../../api/models/kyc-source-of-funds-observations-dto';
import { saveRiskResults } from '../../api/fn/client-risk-matrix/save-risk-results';
import { ClientRiskMatrixResultsResponseDto } from '../../api/models/client-risk-matrix-results-response-dto';
import { RISK_RESULTS_DATE_BEFORE_ASSESSMENT, RISK_RESULTS_LOCKED } from './process-risk-results';
import { KycGeographicRiskAnalysisDto } from '../../api/models/kyc-geographic-risk-analysis-dto';
import { KycFinalObservationsDto } from '../../api/models/kyc-final-observations-dto';
import { PROCESS_OBSERVATION_I18N, ProcessObservationKind } from '../../models/process-observation.model';
import { selectComplianceReportAwaited, selectSelectedProcess } from './process.selectors';
import { listByProcess as listScreeningsByProcess } from '../../api/fn/screenings/list-by-process';
import { run as runScreenings } from '../../api/fn/screenings/run';
import { worldCheckOneCases } from '../../api/fn/screenings/world-check-one-cases';
import { getStatistics } from '../../api/fn/kyc-process/get-statistics';
import { getReport } from '../../api/fn/kyc-report/get-report';
import { getComplianceReport } from '../../api/fn/kyc-compliance-report/get-compliance-report';
import { KycComplianceReportDto } from '../../api/models/kyc-compliance-report-dto';
import { requestSignature as requestSignatureFn } from '../../api/fn/kyc-entrust-signature/request-signature';
import { getSignatureStatus } from '../../api/fn/kyc-entrust-signature/get-signature-status';
import { getEvidenceFolders } from '../../api/fn/kyc-entrust-signature/get-evidence-folders';
import { skipSignature as skipSignatureFn } from '../../api/fn/kyc-entrust-signature/skip-signature';
import { EntrustSignatureStatusResponseDto } from '../../api/models/entrust-signature-status-response-dto';
import { EntrustSignatureStartResponseDto } from '../../api/models/entrust-signature-start-response-dto';
import { EntrustSignatureEvidenceFolderDto } from '../../api/models/entrust-signature-evidence-folder-dto';
import { KycProcessStatisticsDto } from '../../api/models/kyc-process-statistics-dto';
import { KycReportDto } from '../../api/models/kyc-report-dto';
import { KycProcessDto } from '../../api/models/kyc-process-dto';
import { KycConsentEvidenceDto } from '../../api/models/kyc-consent-evidence-dto';
import { getConsentEvidence } from '../../api/fn/kyc-client-consent-evidence/get-consent-evidence';
import { uploadConsentEvidence } from '../../api/fn/kyc-client-consent-evidence/upload-consent-evidence';
import { CONSENT_EVIDENCE_LOCKED } from './process-consent-evidence';
import { KycProcessListRow } from './process.state';
import { KycPersonFormDto } from '../../api/models/kyc-person-form-dto';
import { KycCompanyFormDto } from '../../api/models/kyc-company-form-dto';
import { KycChangeRequestWithItemsDto } from '../../api/models/kyc-change-request-with-items-dto';
import { KycChangeRequestDto } from '../../api/models/kyc-change-request-dto';
import { KycProcessApprovalStateDto } from '../../api/models/kyc-process-approval-state-dto';
import { KycProcessApprovalDto } from '../../api/models/kyc-process-approval-dto';
import { KycWorkflowEventDto } from '../../api/models/kyc-workflow-event-dto';
import { KycProcessParticipantDto } from '../../api/models/kyc-process-participant-dto';
import { ClientRiskMatrixStateResponseDto } from '../../api/models/client-risk-matrix-state-response-dto';
import { ClientRiskMatrixAssessResponseDto } from '../../api/models/client-risk-matrix-assess-response-dto';
import { ClientRiskMatrixMeasuresResponseDto } from '../../api/models/client-risk-matrix-measures-response-dto';
import { GroupedScreeningsDto } from '../../api/models/grouped-screenings-dto';
import { ScreeningsRunResponseDto } from '../../api/models/screenings-run-response-dto';
import { WorldCheckOneCasesResponseDto } from '../../api/models/world-check-one-cases-response-dto';
import { selectHasPermission, selectUserRoles } from '../user/user.selectors';
import { Permission } from '../../models/permission.config';
import { UserRole } from '../../models/user-role.enum';
import { errorMessage, failWith, groupedErrorMessage, toApiError } from '../../utils/api-error.utils';

const KYC_REPORT_POLL_INTERVAL_MS = 10_000;
/** Re-checks of the full compliance report while one is awaited (US-003): the backend job runs every minute. */
export const COMPLIANCE_REPORT_POLL_INTERVAL_MS = 20_000;
/** …for at most 5 minutes: then the page shows the last answer until it is reloaded or the analysis changes again. */
export const COMPLIANCE_REPORT_MAX_POLLS = 15;
const SIGNATURE_POLL_INTERVAL_MS = 10_000;

/** Save-measures rejections meaning the latest assessment changed under the open screen. */
const STALE_RISK_MEASURES_CODES = [
  'error.kyc.risk-matrix.no-assessment',
  'error.kyc.risk-matrix.measures-reassessment-required',
  'error.kyc.risk-matrix.measures-not-recommended',
];

/** Measures refused because the process is REJECTED (US-002): its status changed under the open page. */
const RISK_MEASURES_LOCKED = 'error.kyc.risk-matrix.measures-locked';

/** Observations refused because the process is REJECTED (US-004, US-008; COMPLETED no longer, US-002): its status changed under the
 *  open page. */
const OBSERVATIONS_LOCKED = [
  'error.kyc.process.source-of-funds-observations-locked',
  'error.kyc.process.geographic-risk-analysis-locked',
  'error.kyc.process.final-observations-locked',
];

/** The Compliance Officer's observations (US-005, US-009): each kind's endpoints and the DTO field holding its text. */
const OBSERVATION_ENDPOINTS: Record<ProcessObservationKind, {
  field: string;
  get: (http: HttpClient, rootUrl: string, processId: string) => Observable<StrictHttpResponse<unknown>>;
  save: (http: HttpClient, rootUrl: string, processId: string, body: Record<string, string>) =>
    Observable<StrictHttpResponse<unknown>>;
}> = {
  sourceOfFunds: {
    field: 'observations' satisfies keyof KycSourceOfFundsObservationsDto,
    get: (http, rootUrl, processId) => getSourceOfFundsObservations(http, rootUrl, { processId }),
    save: (http, rootUrl, processId, body) => saveSourceOfFundsObservations(http, rootUrl, { processId, body }),
  },
  geographicRiskAnalysis: {
    field: 'analysis' satisfies keyof KycGeographicRiskAnalysisDto,
    get: (http, rootUrl, processId) => getGeographicRiskAnalysis(http, rootUrl, { processId }),
    save: (http, rootUrl, processId, body) => saveGeographicRiskAnalysis(http, rootUrl, { processId, body }),
  },
  finalObservations: {
    field: 'observations' satisfies keyof KycFinalObservationsDto,
    get: (http, rootUrl, processId) => getFinalObservations(http, rootUrl, { processId }),
    save: (http, rootUrl, processId, body) => saveFinalObservations(http, rootUrl, { processId, body }),
  },
};

const CLIENT_REOPEN_SIGNATURE_STARTED = 'error.kyc.process.client-reopen-signature-started';
/** Refusal shared by the officer and the client reopen: the process is not FOR_APPROVAL or COMPLETED. */
const REOPEN_NOT_ALLOWED = 'error.kyc.process.reopen-not-allowed';

/** Client reopen refusals shown with the frontend's own text (FLOW_BUSY: another signature
 *  request or skip is running, so "try again in a moment"). */
const CLIENT_REOPEN_ERROR_KEYS: Record<string, string> = {
  [CLIENT_REOPEN_SIGNATURE_STARTED]: 'PROCESS.ERROR.CLIENT_REOPEN_SIGNATURE_STARTED',
  [REOPEN_NOT_ALLOWED]: 'PROCESS.ERROR.REOPEN_NOT_ALLOWED',
  'error.kyc.signature.flow-busy': 'PROCESS.ERROR.FLOW_BUSY',
};

function parseResponseBody<T>(body: unknown): Observable<T> {
  if (body instanceof Blob) {
    return from(body.text()).pipe(map((text) => (text ? JSON.parse(text) : null) as T));
  }
  return of(body as T);
}

/** The store action for a World-Check cases response (null for "no cases yet", e.g. a 404). */
function worldcheckCasesLoaded(body: WorldCheckOneCasesResponseDto | null) {
  const cases = body?.cases ?? [];
  const finalStatus = body?.finalStatus ?? null;
  const missingTargets = body?.missingTargets ?? [];
  return cases.length > 0
    ? ProcessActions.loadWorldcheckCasesSuccess({ cases, finalStatus, missingTargets })
    : ProcessActions.loadWorldcheckCasesEmpty({ finalStatus, missingTargets });
}

@Injectable()
export class ProcessEffects {
  private readonly actions$ = inject(Actions);
  private readonly http = inject(HttpClient);
  private readonly apiConfig = inject(ApiConfiguration);
  private readonly store = inject(Store);
  private readonly translate = inject(TranslateService);
  private readonly cancelProcessScopedRequests$ = this.actions$.pipe(
    ofType(ProcessActions.selectProcess, ProcessActions.clearProcesses),
  );

  readonly loadProcesses$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProcessActions.loadProcesses),
      withLatestFrom(this.store.select(selectUserRoles)),
      switchMap(([{ managementCompanyId }, roles]) => {
        const isClient = roles.includes(UserRole.CLIENT);

        if (isClient) {
          // CLIENT endpoint still returns the full KycProcessDto[]. Map it down
          // to KycProcessListRow so state.processes has a single shape.
          return getMyProcesses(this.http, this.apiConfig.rootUrl, {}).pipe(
            map((response) => {
              const body = (response.body as unknown as KycProcessDto[]) ?? [];
              const processes: KycProcessListRow[] = body.map((p) => ({
                processId: p.id,
                clientName: p.customerName,
                clientType: p.clientType,
                companyName: p.companyName,
                fundName: p.fundName,
                riskLevel: p.riskLevelCurrent,
                status: p.status,
                createdAt: p.createdAt,
                updatedAt: p.updatedAt,
                revalidationDate: p.refreshDueAt,
              }));
              return ProcessActions.loadProcessesSuccess({ processes });
            }),
            catchError(failWith('PROCESS.ERROR.LOAD_PROCESSES', (error) => ProcessActions.loadProcessesFailure({ error })))
          );
        }

        return getAll2(this.http, this.apiConfig.rootUrl, { managementCompanyId }).pipe(
          map((response) => {
            const processes: KycProcessListRow[] = (response.body as unknown as KycProcessListRow[]) ?? [];
            return ProcessActions.loadProcessesSuccess({ processes });
          }),
          catchError(failWith('PROCESS.ERROR.LOAD_PROCESSES', (error) => ProcessActions.loadProcessesFailure({ error })))
        );
      })
    )
  );

  readonly loadProcess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProcessActions.loadProcess),
      switchMap(({ processId }) =>
        getById3(this.http, this.apiConfig.rootUrl, { id: processId }).pipe(
          map((response) => {
            const process: KycProcessDto = response.body as KycProcessDto;
            return ProcessActions.loadProcessSuccess({ process });
          }),
          catchError(failWith('PROCESS.ERROR.LOAD_PROCESS', (error, apiError) =>
            ProcessActions.loadProcessFailure({ error, status: apiError.status })))
        )
      )
    )
  );

  /** Background refresh used after comment/approval actions. Same API call as
   *  loadProcess but its reducer preserves the rest of the process state so
   *  existing changeRequests / timeline / forms aren't wiped during refetch. */
  readonly refreshProcessStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProcessActions.refreshProcessStatus),
      switchMap(({ processId }) =>
        getById3(this.http, this.apiConfig.rootUrl, { id: processId }).pipe(
          map((response) => {
            const process: KycProcessDto = response.body as KycProcessDto;
            return ProcessActions.refreshProcessStatusSuccess({ process });
          }),
          catchError(failWith('PROCESS.ERROR.REFRESH_STATUS', (error) => ProcessActions.refreshProcessStatusFailure({ error })))
        )
      )
    )
  );

  readonly loadIndividualForm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProcessActions.loadIndividualForm),
      switchMap(({ processId }) =>
        getIndividualForm(this.http, this.apiConfig.rootUrl, { processId }).pipe(
          map((response) => {
            const form: KycPersonFormDto = response.body as KycPersonFormDto;
            return ProcessActions.loadIndividualFormSuccess({ form });
          }),
          catchError(failWith('PROCESS.ERROR.LOAD_INDIVIDUAL_FORM', (error) => ProcessActions.loadIndividualFormFailure({ error })))
        )
      )
    )
  );

  readonly saveIndividualForm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProcessActions.saveIndividualForm),
      switchMap(({ processId, form, silent }) =>
        updateIndividualForm(this.http, this.apiConfig.rootUrl, { processId, body: form }).pipe(
          switchMap((response) => {
            const savedForm: KycPersonFormDto = response.body as KycPersonFormDto;
            return silent
              ? [ProcessActions.saveIndividualFormSuccess({ form: savedForm })]
              : [
                  ProcessActions.saveIndividualFormSuccess({ form: savedForm }),
                  ToastActions.showToast({
                    message: 'PROCESS.FORM.SAVE_SUCCESS',
                    notificationType: 'success',
                  }),
                ];
          }),
          catchError((error) =>
            toApiError(error).pipe(
              mergeMap((apiError) => {
                const message = groupedErrorMessage(apiError, 'PROCESS.ERROR.SAVE_INDIVIDUAL_FORM');
                // Someone else saved the form since it was loaded: no toast, the page asks to reload it (US-024).
                return isFormConflict(apiError.status, apiError.code)
                  ? [
                      ProcessActions.saveIndividualFormFailure({ error: message, code: apiError.code }),
                      ProcessActions.formSaveConflict({ processId, formType: 'individual' }),
                    ]
                  : [ProcessActions.saveIndividualFormFailure({ error: message })];
              }),
            )
          )
        )
      )
    )
  );

  readonly loadCompanyForm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProcessActions.loadCompanyForm),
      switchMap(({ processId, silent }) =>
        getCompanyForm(this.http, this.apiConfig.rootUrl, { processId }).pipe(
          map((response) => {
            const form: KycCompanyFormDto = response.body as KycCompanyFormDto;
            return ProcessActions.loadCompanyFormSuccess({ form });
          }),
          catchError(failWith('PROCESS.ERROR.LOAD_COMPANY_FORM', (error) => ProcessActions.loadCompanyFormFailure({
              error,
              silent,
            })))
        )
      )
    )
  );

  readonly saveCompanyForm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProcessActions.saveCompanyForm),
      switchMap(({ processId, form, silent }) =>
        updateCompanyForm(this.http, this.apiConfig.rootUrl, { processId, body: form }).pipe(
          switchMap((response) => {
            const savedForm: KycCompanyFormDto = response.body as KycCompanyFormDto;
            return silent
              ? [ProcessActions.saveCompanyFormSuccess({ form: savedForm })]
              : [
                  ProcessActions.saveCompanyFormSuccess({ form: savedForm }),
                  ToastActions.showToast({
                    message: 'PROCESS.FORM.SAVE_SUCCESS',
                    notificationType: 'success',
                  }),
                ];
          }),
          catchError((error) =>
            toApiError(error).pipe(
              mergeMap((apiError) => {
                const message = groupedErrorMessage(apiError, 'PROCESS.ERROR.SAVE_COMPANY_FORM');
                return isFormConflict(apiError.status, apiError.code)
                  ? [
                      ProcessActions.saveCompanyFormFailure({ error: message, code: apiError.code }),
                      ProcessActions.formSaveConflict({ processId, formType: 'company' }),
                    ]
                  : [ProcessActions.saveCompanyFormFailure({ error: message })];
              }),
            )
          )
        )
      )
    )
  );

  readonly loadChangeRequests$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProcessActions.loadChangeRequests),
      switchMap(({ processId }) =>
        getAllWithItems(this.http, this.apiConfig.rootUrl, { processId }).pipe(
          switchMap((response) =>
            parseResponseBody<KycChangeRequestWithItemsDto[]>(response.body).pipe(
              map((changeRequests) =>
                ProcessActions.loadChangeRequestsSuccess({ changeRequests: changeRequests ?? [] })
              )
            )
          ),
          catchError(failWith('PROCESS.ERROR.LOAD_CHANGE_REQUESTS', (error) => ProcessActions.loadChangeRequestsFailure({ error })))
        )
      )
    )
  );

  readonly createChangeRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProcessActions.createChangeRequest),
      switchMap(({ processId, body }) =>
        createWithItems(this.http, this.apiConfig.rootUrl, { processId, body }).pipe(
          switchMap((response) =>
            parseResponseBody<KycChangeRequestWithItemsDto>(response.body).pipe(
              switchMap((changeRequest) => [
                ProcessActions.createChangeRequestSuccess({ changeRequest }),
                ProcessActions.refreshProcessStatus({ processId }),
                ToastActions.showToast({
                  message: 'CHANGE_REQUEST.CREATE_SUCCESS',
                  notificationType: 'success',
                }),
              ])
            )
          ),
          catchError(failWith('PROCESS.ERROR.CREATE_CHANGE_REQUEST', (error) => ProcessActions.createChangeRequestFailure({ error })))
        )
      )
    )
  );

  readonly closeChangeRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProcessActions.closeChangeRequest),
      switchMap(({ processId, changeRequestId }) =>
        close(this.http, this.apiConfig.rootUrl, { processId, changeRequestId }).pipe(
          switchMap((response) =>
            parseResponseBody<KycChangeRequestDto>(response.body).pipe(
              switchMap((changeRequest) => [
                ProcessActions.closeChangeRequestSuccess({ changeRequest }),
                ProcessActions.refreshProcessStatus({ processId }),
                ToastActions.showToast({
                  message: 'CHANGE_REQUEST.CLOSE_SUCCESS',
                  notificationType: 'success',
                }),
              ])
            )
          ),
          catchError(failWith('PROCESS.ERROR.CLOSE_CHANGE_REQUEST', (error) => ProcessActions.closeChangeRequestFailure({ error })))
        )
      )
    )
  );

  readonly reopenChangeRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProcessActions.reopenChangeRequest),
      switchMap(({ processId, changeRequestId }) =>
        reopen(this.http, this.apiConfig.rootUrl, { processId, changeRequestId }).pipe(
          switchMap((response) =>
            parseResponseBody<KycChangeRequestDto>(response.body).pipe(
              switchMap((changeRequest) => [
                ProcessActions.reopenChangeRequestSuccess({ changeRequest }),
                ProcessActions.refreshProcessStatus({ processId }),
                ToastActions.showToast({
                  message: 'CHANGE_REQUEST.REOPEN_SUCCESS',
                  notificationType: 'success',
                }),
              ])
            )
          ),
          catchError(failWith('PROCESS.ERROR.REOPEN_CHANGE_REQUEST', (error) => ProcessActions.reopenChangeRequestFailure({ error })))
        )
      )
    )
  );

  readonly transitionStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProcessActions.transitionStatus),
      switchMap(({ processId, body }) =>
        transitionStatus(this.http, this.apiConfig.rootUrl, { id: processId, body }).pipe(
          switchMap((response) => {
            const process: KycProcessDto = response.body as KycProcessDto;
            return [
              ProcessActions.transitionStatusSuccess({ process }),
              ProcessActions.loadTimeline({ processId }),
              ProcessActions.loadApprovalState({ processId }),
              ToastActions.showToast({
                message: 'PROCESS.DETAIL.TRANSITION_SUCCESS',
                notificationType: 'success',
              }),
            ];
          }),
          catchError(failWith('PROCESS.ERROR.TRANSITION_STATUS', (error) => ProcessActions.transitionStatusFailure({ error })))
        )
      )
    )
  );

  readonly reopenProcess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProcessActions.reopenProcess),
      switchMap(({ processId, body }) =>
        reopenProcess(this.http, this.apiConfig.rootUrl, { id: processId, body }).pipe(
          switchMap((response) => {
            const process: KycProcessDto = response.body as KycProcessDto;
            const reloadForm = process.clientType === 'COMPANY'
              ? ProcessActions.loadCompanyForm({ processId })
              : ProcessActions.loadIndividualForm({ processId });
            return [
              ProcessActions.reopenProcessSuccess({ process }),
              ProcessActions.loadTimeline({ processId }),
              ProcessActions.loadApprovalState({ processId }),
              ProcessActions.loadFormVersions({ processId }),
              reloadForm,
              ToastActions.showToast({
                message: 'PROCESS.DETAIL.REOPEN_SUCCESS',
                notificationType: 'success',
              }),
            ];
          }),
          catchError(failWith('PROCESS.ERROR.REOPEN_PROCESS', (error, apiError) => {
            const code = apiError.code;
            const mapped =
              code === 'error.kyc.process.reopen-reason-required' ? 'PROCESS.ERROR.REOPEN_REASON_REQUIRED'
              : code === REOPEN_NOT_ALLOWED ? 'PROCESS.ERROR.REOPEN_NOT_ALLOWED'
              : error;
            return ProcessActions.reopenProcessFailure({ error: mapped });
          }))
        )
      )
    )
  );

  /** The client's own reopen (Edit on a submitted process). Mirrors reopenProcess$, without a body;
   *  exhaustMap ignores a second click while the first request runs. */
  readonly clientReopenProcess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProcessActions.clientReopenProcess),
      exhaustMap(({ processId }) =>
        clientReopen(this.http, this.apiConfig.rootUrl, { id: processId }).pipe(
          // The new selectedProcess already makes the page reload the form (and the approval state is
          // staff-only), so only the timeline and form versions are reloaded here.
          switchMap((response) => [
            ProcessActions.clientReopenProcessSuccess({ process: response.body as KycProcessDto }),
            ProcessActions.loadTimeline({ processId }),
            ProcessActions.loadFormVersions({ processId }),
            ToastActions.showToast({
              message: 'PROCESS.CLIENT_EDIT.SUCCESS',
              notificationType: 'success',
            }),
          ]),
          catchError(failWith('PROCESS.ERROR.CLIENT_REOPEN_PROCESS', (error, apiError) =>
            ProcessActions.clientReopenProcessFailure({
              processId,
              error: (apiError.code && CLIENT_REOPEN_ERROR_KEYS[apiError.code]) || error,
              code: apiError.code,
            })
          ))
        )
      )
    )
  );

  /** A refused client reopen means the page was out of date: the process moved on (not reopenable
   *  anymore) or a signature was requested meanwhile. Reload what the Edit action depends on. */
  readonly reloadAfterRefusedClientReopen$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProcessActions.clientReopenProcessFailure),
      switchMap(({ processId, code }) => {
        if (code === CLIENT_REOPEN_SIGNATURE_STARTED) {
          return [
            ProcessActions.refreshProcessStatus({ processId }),
            ProcessActions.loadSignatureStatus({ processId }),
          ];
        }
        return code === REOPEN_NOT_ALLOWED ? [ProcessActions.refreshProcessStatus({ processId })] : [];
      })
    )
  );

  readonly loadFormVersions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProcessActions.loadFormVersions),
      switchMap(({ processId }) =>
        listFormVersions(this.http, this.apiConfig.rootUrl, { id: processId }).pipe(
          map((response) => ProcessActions.loadFormVersionsSuccess({
            versions: (response.body ?? []) as KycFormSnapshotSummaryDto[],
          })),
          catchError(failWith('PROCESS.ERROR.LOAD_FORM_VERSIONS', (error) => ProcessActions.loadFormVersionsFailure({ error })))
        )
      )
    )
  );

  readonly loadFormVersionDetail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProcessActions.loadFormVersionDetail),
      switchMap(({ processId, snapshotId }) =>
        getFormVersion(this.http, this.apiConfig.rootUrl, { id: processId, snapshotId }).pipe(
          map((response) => ProcessActions.loadFormVersionDetailSuccess({
            version: response.body as KycFormSnapshotDto,
          })),
          catchError(failWith('PROCESS.ERROR.LOAD_FORM_VERSION_DETAIL', (error, apiError) => {
            const mapped = apiError.code === 'error.kyc.process.form-snapshot-not-found'
              ? 'PROCESS.ERROR.FORM_SNAPSHOT_NOT_FOUND'
              : error;
            return ProcessActions.loadFormVersionDetailFailure({ error: mapped });
          }))
        )
      )
    )
  );

  readonly submitProcess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProcessActions.submitProcess),
      switchMap(({ processId, body }) =>
        submit(this.http, this.apiConfig.rootUrl, { id: processId, body }).pipe(
          switchMap((response) => {
            const process: KycProcessDto = response.body as KycProcessDto;
            return [
              ProcessActions.submitProcessSuccess({ process }),
              ProcessActions.loadTimeline({ processId }),
              ProcessActions.loadApprovalState({ processId }),
              ToastActions.showToast({
                message: 'PROCESS.DETAIL.SUBMIT_SUCCESS',
                notificationType: 'success',
              }),
            ];
          }),
          catchError(failWith('PROCESS.ERROR.SUBMIT_PROCESS', (error) => ProcessActions.submitProcessFailure({ error })))
        )
      )
    )
  );

  /** US-026: a Compliance Officer creates a process without a client. The dashboard's list and statistics are
   *  reloaded; the dialog waits for the outcome (and opens the new process on success). */
  readonly createProcessWithoutClient$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProcessActions.createProcessWithoutClient),
      exhaustMap(({ body }) =>
        createWithoutClient(this.http, this.apiConfig.rootUrl, { body }).pipe(
          switchMap((response) => [
            ProcessActions.createProcessWithoutClientSuccess({ process: response.body as KycProcessDto }),
            ProcessActions.loadProcesses({ managementCompanyId: body.managementCompanyId }),
            ProcessActions.loadProcessStatistics({ managementCompanyId: body.managementCompanyId }),
            ToastActions.showToast({ message: 'CLIENT.WITHOUT_CLIENT.SUCCESS', notificationType: 'success' }),
          ]),
          catchError((error) => toApiError(error).pipe(
            map((apiError) => ProcessActions.createProcessWithoutClientFailure({
              error: errorMessage(apiError, 'CLIENT.WITHOUT_CLIENT.FAILED'),
              fieldErrors: apiError.fieldErrors,
            })),
          )),
        )
      )
    )
  );

  /** US-025: a Compliance Officer deletes a process (soft delete, with an internal reason). On 204 the reducer drops its
   *  row from the list and a success toast shows; what else reloads is up to the caller. The dialog and the page
   *  handle the outcome (no toast for a failure here). */
  readonly deleteProcess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProcessActions.deleteProcess),
      exhaustMap(({ processId, reason }) =>
        delete3(this.http, this.apiConfig.rootUrl, { id: processId, body: { reason } }).pipe(
          // From the process page, the app goes back to the dashboard, which loads the list and (with kpi:read) the
          // statistics. From the dashboard's own list, the row is dropped here and only the statistics reload.
          switchMap(() => [
            ProcessActions.deleteProcessSuccess({ processId }),
            ToastActions.showToast({ message: 'PROCESS.DELETE.SUCCESS', notificationType: 'success' }),
          ]),
          catchError((error) => toApiError(error).pipe(
            map((apiError) => ProcessActions.deleteProcessFailure({
              processId,
              error: errorMessage(apiError, 'PROCESS.DELETE.FAILED'),
              status: apiError.status,
              code: apiError.code,
            })),
          )),
        )
      )
    )
  );

  /** 123y1w8px43: a Pending row's invitation withdrawn (123y1w8rxzz). The row is dropped here and the dashboard reloads
   *  only the statistics, as for a deleted process; the same success toast (the same texts as the delete). */
  readonly withdrawInvitation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProcessActions.withdrawInvitation),
      exhaustMap(({ customerId, reason }) =>
        withdrawCustomerInvitation(this.http, this.apiConfig.rootUrl, { customerId, body: { reason } }).pipe(
          switchMap(() => [
            ProcessActions.withdrawInvitationSuccess({ customerId }),
            ToastActions.showToast({ message: 'PROCESS.DELETE.SUCCESS', notificationType: 'success' }),
          ]),
          catchError((error) => toApiError(error).pipe(
            map((apiError) => ProcessActions.withdrawInvitationFailure({
              customerId,
              error: errorMessage(apiError, 'PROCESS.DELETE.FAILED'),
              status: apiError.status,
              code: apiError.code,
            })),
          )),
        )
      )
    )
  );

  // Approval Workflow
  readonly loadApprovalState$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProcessActions.loadApprovalState),
      withLatestFrom(this.store.select(selectUserRoles)),
      // CLIENT users have no PROCESS_ACCEPTANCE permission (approvals are an
      // internal compliance/admin workflow), so skip the request rather than
      // surface a 403 as an error toast after a successful submit.
      filter(([, roles]) => !roles.includes(UserRole.CLIENT)),
      switchMap(([{ processId }]) =>
        getApprovalState(this.http, this.apiConfig.rootUrl, { processId }).pipe(
          map((response) => {
            const approvalState: KycProcessApprovalStateDto = response.body as KycProcessApprovalStateDto;
            return ProcessActions.loadApprovalStateSuccess({ approvalState });
          }),
          catchError(failWith('PROCESS.ERROR.LOAD_APPROVAL_STATE', (error) => ProcessActions.loadApprovalStateFailure({ error })))
        )
      )
    )
  );

  readonly loadApprovals$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProcessActions.loadApprovals),
      switchMap(({ processId }) =>
        getApprovals(this.http, this.apiConfig.rootUrl, { processId }).pipe(
          switchMap((response) =>
            parseResponseBody<KycProcessApprovalDto[]>(response.body).pipe(
              map((approvals) =>
                ProcessActions.loadApprovalsSuccess({ approvals: approvals ?? [] })
              )
            )
          ),
          catchError(failWith('PROCESS.ERROR.LOAD_APPROVALS', (error) => ProcessActions.loadApprovalsFailure({ error })))
        )
      )
    )
  );

  readonly decideApproval$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProcessActions.decideApproval),
      switchMap(({ processId, body }) =>
        decide(this.http, this.apiConfig.rootUrl, { processId, body }).pipe(
          switchMap((response) => {
            const approval: KycProcessApprovalDto = response.body as KycProcessApprovalDto;
            return [
              ProcessActions.decideApprovalSuccess({ approval }),
              ProcessActions.loadApprovalState({ processId }),
              ProcessActions.loadTimeline({ processId }),
              ProcessActions.refreshProcessStatus({ processId }),
              ToastActions.showToast({
                message: 'PROCESS.APPROVAL.DECISION_SUCCESS',
                notificationType: 'success',
              }),
            ];
          }),
          catchError(failWith('PROCESS.ERROR.DECIDE_APPROVAL', (error) => ProcessActions.decideApprovalFailure({ error })))
        )
      )
    )
  );

  // Timeline
  readonly loadTimeline$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProcessActions.loadTimeline),
      switchMap(({ processId }) =>
        getTimeline(this.http, this.apiConfig.rootUrl, { id: processId }).pipe(
          switchMap((response) =>
            parseResponseBody<KycWorkflowEventDto[]>(response.body).pipe(
              map((events) =>
                ProcessActions.loadTimelineSuccess({ events: events ?? [] })
              )
            )
          ),
          catchError(failWith('PROCESS.ERROR.LOAD_TIMELINE', (error) => ProcessActions.loadTimelineFailure({ error })))
        )
      )
    )
  );

  // Participants
  readonly loadParticipants$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProcessActions.loadParticipants),
      switchMap(({ processId }) =>
        getAllByProcessId(this.http, this.apiConfig.rootUrl, { processId }).pipe(
          switchMap((response) =>
            parseResponseBody<KycProcessParticipantDto[]>(response.body).pipe(
              map((participants) =>
                ProcessActions.loadParticipantsSuccess({ participants: participants ?? [] })
              )
            )
          ),
          catchError(failWith('PROCESS.ERROR.LOAD_PARTICIPANTS', (error) => ProcessActions.loadParticipantsFailure({ error })))
        )
      )
    )
  );

  // Risk Matrix
  readonly loadRiskMatrixState$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProcessActions.loadRiskMatrixState),
      switchMap(({ processId }) =>
        getState(this.http, this.apiConfig.rootUrl, { kycProcessId: processId }).pipe(
          switchMap((response) =>
            parseResponseBody<ClientRiskMatrixStateResponseDto>(response.body).pipe(
              map((riskMatrixState) =>
                ProcessActions.loadRiskMatrixStateSuccess({ riskMatrixState: riskMatrixState ?? { matrix: [] } })
              )
            )
          ),
          catchError(failWith('PROCESS.ERROR.LOAD_RISK_MATRIX_STATE', (error) => ProcessActions.loadRiskMatrixStateFailure({ error })))
        )
      )
    )
  );

  readonly assessRiskMatrix$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProcessActions.assessRiskMatrix),
      switchMap(({ processId, body }) =>
        assess(this.http, this.apiConfig.rootUrl, { body }).pipe(
          switchMap((response) =>
            parseResponseBody<ClientRiskMatrixAssessResponseDto>(response.body).pipe(
              switchMap((assessResponse) => [
                ProcessActions.assessRiskMatrixSuccess({ response: assessResponse }),
                ProcessActions.loadRiskMatrixState({ processId }),
                // A new assessment can change the recommended measures and drop their answers.
                ProcessActions.loadRiskMeasures({ processId }),
              ])
            )
          ),
          catchError(failWith('PROCESS.ERROR.ASSESS_RISK_MATRIX', (error) => ProcessActions.assessRiskMatrixFailure({ error })))
        )
      )
    )
  );

  // Risk Measures
  readonly loadRiskMeasures$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProcessActions.loadRiskMeasures),
      switchMap(({ processId }) =>
        getRiskMeasures(this.http, this.apiConfig.rootUrl, { kycProcessId: processId }).pipe(
          switchMap((response) =>
            parseResponseBody<ClientRiskMatrixMeasuresResponseDto>(response.body).pipe(
              map((riskMeasures) =>
                ProcessActions.loadRiskMeasuresSuccess({ riskMeasures: riskMeasures ?? { options: [] } })
              )
            )
          ),
          catchError(failWith('PROCESS.ERROR.LOAD_RISK_MEASURES', (error) => ProcessActions.loadRiskMeasuresFailure({ error })))
        )
      )
    )
  );

  readonly saveRiskMeasures$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProcessActions.saveRiskMeasures),
      switchMap(({ processId, body }) =>
        saveRiskMeasures(this.http, this.apiConfig.rootUrl, { body }).pipe(
          switchMap((response) =>
            parseResponseBody<ClientRiskMatrixMeasuresResponseDto>(response.body).pipe(
              map((riskMeasures) =>
                ProcessActions.saveRiskMeasuresSuccess({ riskMeasures: riskMeasures ?? { options: [] } })
              )
            )
          ),
          catchError(failWith('PROCESS.ERROR.SAVE_RISK_MEASURES', (error, apiError) =>
            ProcessActions.saveRiskMeasuresFailure({ processId, error, code: apiError.code })))
        )
      )
    )
  );

  // Risk assessment results set by hand (US-011)
  readonly saveRiskResults$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProcessActions.saveRiskResults),
      // A save is a write: a second one waits for nothing and is ignored while the first runs.
      exhaustMap(({ processId, body }) =>
        saveRiskResults(this.http, this.apiConfig.rootUrl, { body }).pipe(
          switchMap((response) =>
            parseResponseBody<ClientRiskMatrixResultsResponseDto>(response.body).pipe(
              switchMap((results) => [
                ProcessActions.saveRiskResultsSuccess({ processId, results: results ?? {} }),
                // No results in the answer: the panel reads them again rather than showing none.
                ...(results ? [] : [ProcessActions.loadRiskMatrixState({ processId })]),
                // Other measures bring their own options, and the answers to the previous ones are dropped.
                ProcessActions.loadRiskMeasures({ processId }),
                ToastActions.showToast({
                  message: 'PROCESS.RISK_MATRIX.RESULTS.SAVE_SUCCESS',
                  notificationType: 'success',
                }),
              ])
            )
          ),
          catchError(failWith('PROCESS.ERROR.SAVE_RISK_RESULTS', (error, apiError) =>
            ProcessActions.saveRiskResultsFailure({ processId, error, code: apiError.code })))
        )
      )
    )
  );

  /** Refused as locked: the process was completed or rejected elsewhere, so refresh its status and the results turn
   *  read-only. */
  readonly refreshAfterLockedRiskResults$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProcessActions.saveRiskResultsFailure),
      filter(({ code }) => code === RISK_RESULTS_LOCKED),
      // Only for the process still open: refreshing another would put it on this page.
      withLatestFrom(this.store.select(selectSelectedProcess)),
      filter(([{ processId }, selected]) => selected?.id === processId),
      map(([{ processId }]) => ProcessActions.refreshProcessStatus({ processId }))
    )
  );

  // The Compliance Officer's observations (US-005, US-009). Each kind runs on its own, so cards load and save side by
  // side.
  readonly loadObservation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProcessActions.loadObservation),
      groupBy(({ kind }) => kind),
      mergeMap((byKind) => byKind.pipe(
        switchMap(({ processId, kind }) => {
          const endpoint = OBSERVATION_ENDPOINTS[kind];
          return endpoint.get(this.http, this.apiConfig.rootUrl, processId).pipe(
            switchMap((response) =>
              parseResponseBody<Record<string, string | null | undefined>>(response.body).pipe(
                map((dto) => ProcessActions.loadObservationSuccess({ processId, kind, text: dto?.[endpoint.field] ?? null }))
              )
            ),
            catchError(failWith(`PROCESS.ERROR.LOAD_${PROCESS_OBSERVATION_I18N[kind]}`, (error) =>
              ProcessActions.loadObservationFailure({ processId, kind, error })))
          );
        })
      ))
    )
  );

  readonly saveObservation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProcessActions.saveObservation),
      groupBy(({ kind }) => kind),
      // A save is a write: a second one of the same kind waits for nothing and is ignored while the first runs.
      mergeMap((byKind) => byKind.pipe(
        exhaustMap(({ processId, kind, text }) => {
          const endpoint = OBSERVATION_ENDPOINTS[kind];
          // None: the field left out, which the backend reads as null and clears.
          return endpoint.save(this.http, this.apiConfig.rootUrl, processId, text ? { [endpoint.field]: text } : {}).pipe(
            switchMap((response) =>
              parseResponseBody<Record<string, string | null | undefined>>(response.body).pipe(
                switchMap((dto) => [
                  ProcessActions.saveObservationSuccess({ processId, kind, text: dto?.[endpoint.field] ?? null }),
                  ToastActions.showToast({
                    message: `PROCESS.${PROCESS_OBSERVATION_I18N[kind]}.SAVE_SUCCESS`,
                    notificationType: 'success',
                  }),
                ])
              )
            ),
            catchError(failWith(`PROCESS.ERROR.SAVE_${PROCESS_OBSERVATION_I18N[kind]}`, (error, apiError) =>
              ProcessActions.saveObservationFailure({ processId, kind, error, code: apiError.code })))
          );
        })
      ))
    )
  );

  /** Refused as locked: the process was rejected elsewhere, so refresh its status and the card turns read-only. */
  readonly refreshAfterLockedObservations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProcessActions.saveObservationFailure),
      filter(({ code }) => !!code && OBSERVATIONS_LOCKED.includes(code)),
      // Only for the process still open: refreshing another would put it on this page.
      withLatestFrom(this.store.select(selectSelectedProcess)),
      filter(([{ processId }, selected]) => selected?.id === processId),
      map(([{ processId }]) => ProcessActions.refreshProcessStatus({ processId }))
    )
  );

  /** The save was rejected because the screen is out of date (e.g. the process was reassessed
   *  elsewhere): reload the conclusion and the measures the latest assessment recommends. */
  readonly reloadStaleRiskMeasures$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProcessActions.saveRiskMeasuresFailure),
      filter(({ code }) => !!code && STALE_RISK_MEASURES_CODES.includes(code)),
      switchMap(({ processId }) => [
        ProcessActions.loadRiskMatrixState({ processId }),
        ProcessActions.loadRiskMeasures({ processId }),
      ])
    )
  );

  /** Refused as locked (US-002): the process was rejected elsewhere, so refresh its status and the measures turn
   *  read-only, like the observations and the results. */
  readonly refreshAfterLockedRiskMeasures$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProcessActions.saveRiskMeasuresFailure),
      filter(({ code }) => code === RISK_MEASURES_LOCKED),
      // Only for the process still open: refreshing another would put it on this page.
      withLatestFrom(this.store.select(selectSelectedProcess)),
      filter(([{ processId }, selected]) => selected?.id === processId),
      map(([{ processId }]) => ProcessActions.refreshProcessStatus({ processId }))
    )
  );

  // Screenings
  readonly loadScreenings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProcessActions.loadScreenings),
      switchMap(({ processId }) =>
        listScreeningsByProcess(this.http, this.apiConfig.rootUrl, { kycProcessId: processId }).pipe(
          switchMap((response) =>
            parseResponseBody<GroupedScreeningsDto[]>(response.body).pipe(
              map((screenings) =>
                ProcessActions.loadScreeningsSuccess({ screenings: screenings ?? [] })
              )
            )
          ),
          catchError(failWith('PROCESS.ERROR.LOAD_SCREENINGS', (error) => ProcessActions.loadScreeningsFailure({ error })))
        )
      )
    )
  );

  readonly loadWorldcheckCases$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProcessActions.loadWorldcheckCases),
      switchMap(({ processId }) =>
        worldCheckOneCases(this.http, this.apiConfig.rootUrl, { kycProcessId: processId }).pipe(
          switchMap((response) =>
            parseResponseBody<WorldCheckOneCasesResponseDto | null>(response.body).pipe(
              map(worldcheckCasesLoaded)
            )
          ),
          catchError((error) => {
            const status = (error as { status?: number } | null)?.status;
            if (status === 404) {
              return of(worldcheckCasesLoaded(null));
            }
            return failWith('PROCESS.ERROR.LOAD_WORLDCHECK_CASES', (message) => ProcessActions.loadWorldcheckCasesFailure({ error: message }))(error);
          })
        )
      )
    )
  );

  readonly runScreenings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProcessActions.runScreenings),
      switchMap(({ processId }) =>
        runScreenings(this.http, this.apiConfig.rootUrl, { body: { kycProcessId: processId } }).pipe(
          switchMap((response) =>
            parseResponseBody<ScreeningsRunResponseDto>(response.body).pipe(
              switchMap((res) => {
                const safe = res ?? { screenings: [] };
                const failedProviders = (safe.errors ?? [])
                  .map((e) => e.provider)
                  .filter((provider): provider is string => !!provider)
                  .map((provider) => this.translate.instant(`PROCESS.SCREENINGS.PROVIDER.${provider}`));
                const toast = !safe.errors?.length
                  ? ToastActions.showToast({ message: 'PROCESS.SCREENINGS.RUN_SUCCESS', notificationType: 'success' })
                  : failedProviders.length
                    ? ToastActions.showToast({
                        message: 'PROCESS.SCREENINGS.RUN_PARTIAL_FAILURE_PROVIDERS',
                        messageParams: { providers: failedProviders.join(', ') },
                        notificationType: 'warning',
                      })
                    : ToastActions.showToast({ message: 'PROCESS.SCREENINGS.RUN_PARTIAL_FAILURE', notificationType: 'warning' });
                // The response holds only the newly saved rows, so reload the full lists and the
                // World-Check status (which may now be running again, restarting the polling), and
                // the process, whose screeningOutdated a full re-run clears.
                return [
                  ProcessActions.runScreeningsSuccess({ response: safe }),
                  toast,
                  ProcessActions.loadScreenings({ processId }),
                  ProcessActions.loadWorldcheckCases({ processId }),
                  ProcessActions.refreshProcessStatus({ processId }),
                ];
              })
            )
          ),
          catchError(failWith('PROCESS.ERROR.RUN_SCREENINGS', (error) => ProcessActions.runScreeningsFailure({ error })))
        )
      )
    )
  );

  // KYC Final Report PDF
  readonly loadKycReport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProcessActions.loadKycReport),
      switchMap(({ processId }) =>
        getReport(this.http, this.apiConfig.rootUrl, { processId }).pipe(
          takeUntil(this.cancelProcessScopedRequests$),
          switchMap((response) =>
            parseResponseBody<KycReportDto>(response.body).pipe(
              map((report) =>
                report
                  ? ProcessActions.loadKycReportSuccess({ report })
                  : ProcessActions.loadKycReportEmpty()
              )
            )
          ),
          catchError((error) => {
            const status = (error as { status?: number } | null)?.status;
            if (status === 404) {
              return of(ProcessActions.loadKycReportEmpty());
            }
            return failWith('PROCESS.ERROR.LOAD_KYC_REPORT', (message) => ProcessActions.loadKycReportFailure({ error: message }))(error);
          })
        )
      )
    )
  );

  /** Fetch the KYC final report whenever the process reaches COMPLETED.
   *  Fires on initial load, on background refresh, and after any action that
   *  transitions the process (submit/transition/approval decision). */
  readonly loadKycReportOnCompleted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        ProcessActions.loadProcessSuccess,
        ProcessActions.refreshProcessStatusSuccess,
        ProcessActions.transitionStatusSuccess,
        ProcessActions.submitProcessSuccess,
      ),
      filter(({ process }) => process.status === 'COMPLETED' && !!process.id),
      map(({ process }) => ProcessActions.loadKycReport({ processId: process.id! }))
    )
  );

  /** Re-check the External Screenings until they are done, so Accept enables without a
   *  page reload (a FAILED screening is retried by the backend). Runs until cancelled.
   *  Each tick fetches provider results and World-Check cases together and stores them in
   *  one go: one list arriving before the other would briefly read as a failed screening.
   *  Silent: no spinners, and a failed tick is dropped (the next one tries again). */
  readonly pollScreenings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProcessActions.startScreeningsPolling),
      switchMap(({ processId, intervalMs }) =>
        timer(intervalMs, intervalMs).pipe(
          switchMap(() =>
            forkJoin({
              screenings: listScreeningsByProcess(this.http, this.apiConfig.rootUrl, { kycProcessId: processId }).pipe(
                switchMap((response) => parseResponseBody<GroupedScreeningsDto[]>(response.body)),
              ),
              worldcheck: worldCheckOneCases(this.http, this.apiConfig.rootUrl, { kycProcessId: processId }).pipe(
                switchMap((response) => parseResponseBody<WorldCheckOneCasesResponseDto | null>(response.body)),
                // As in loadWorldcheckCases$: a 404 means no cases yet.
                catchError((error) => (error as { status?: number } | null)?.status === 404
                  ? of(null)
                  : throwError(() => error)),
              ),
            }).pipe(
              mergeMap(({ screenings, worldcheck }) => [
                ProcessActions.loadScreeningsSuccess({ screenings: screenings ?? [] }),
                worldcheckCasesLoaded(worldcheck),
              ]),
              catchError(() => EMPTY),
            ),
          ),
          // After the requests, so a tick still in flight is dropped too: its late
          // result must not land on another process.
          takeUntil(this.actions$.pipe(ofType(ProcessActions.stopScreeningsPolling))),
          takeUntil(this.cancelProcessScopedRequests$),
        ),
      ),
    )
  );

  /** Long-poll the KYC final report endpoint while the process is COMPLETED
   *  but the report has not yet been generated by the backend. Stops on
   *  successful load or when explicitly cancelled (e.g. on page leave). */
  readonly pollKycReport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProcessActions.startKycReportPolling),
      switchMap(({ processId }) =>
        timer(KYC_REPORT_POLL_INTERVAL_MS, KYC_REPORT_POLL_INTERVAL_MS).pipe(
          takeUntil(
            this.actions$.pipe(
              ofType(
                ProcessActions.stopKycReportPolling,
                ProcessActions.loadKycReportSuccess,
              ),
            ),
          ),
          map(() => ProcessActions.loadKycReport({ processId })),
        ),
      ),
    )
  );

  // ── Full compliance KYC report (US-003 on US-002) ──

  /** 404: none yet (not COMPLETED, reopened, or still being generated). Other failures: the backend's message, shown
   *  in the section with a retry (no toast); a background load's (`silent`) changes nothing shown. */
  readonly loadComplianceReport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProcessActions.loadComplianceReport),
      switchMap(({ processId, poll, silent }) =>
        getComplianceReport(this.http, this.apiConfig.rootUrl, { processId }).pipe(
          takeUntil(this.cancelProcessScopedRequests$),
          switchMap((response) => parseResponseBody<KycComplianceReportDto>(response.body)),
          map((report) => report?.url
            ? ProcessActions.loadComplianceReportSuccess({ processId, report })
            : ProcessActions.loadComplianceReportEmpty({ processId, poll })),
          catchError((error) => {
            if ((error as { status?: number } | null)?.status === 404) {
              return of(ProcessActions.loadComplianceReportEmpty({ processId, poll }));
            }
            return failWith('PROCESS.ERROR.LOAD_COMPLIANCE_REPORT', (message) =>
              ProcessActions.loadComplianceReportFailure({ processId, error: message, silent: poll || silent }))(error);
          })
        )
      )
    )
  );

  /** A newer report is awaited (none yet on COMPLETED, or the analysis just changed): re-checked every
   *  COMPLIANCE_REPORT_POLL_INTERVAL_MS until it is there, for at most COMPLIANCE_REPORT_MAX_POLLS times; still
   *  awaited one interval after the last, the wait ends (the job is late, or the save changed nothing and nothing is
   *  generated). Started by an answer of none or by a change, not by the re-checks' own answers. */
  readonly pollComplianceReport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProcessActions.loadComplianceReportEmpty, ProcessActions.complianceReportOutdated),
      filter((action) => !('poll' in action && action.poll)),
      switchMap(({ processId }) => {
        const awaited$ = this.store.select(selectComplianceReportAwaited);
        return concat(
          timer(COMPLIANCE_REPORT_POLL_INTERVAL_MS, COMPLIANCE_REPORT_POLL_INTERVAL_MS).pipe(
            take(COMPLIANCE_REPORT_MAX_POLLS),
            withLatestFrom(awaited$),
            takeWhile(([, awaited]) => awaited),
            map(() => ProcessActions.loadComplianceReport({ processId, poll: true })),
          ),
          timer(COMPLIANCE_REPORT_POLL_INTERVAL_MS).pipe(
            withLatestFrom(awaited$),
            filter(([, awaited]) => awaited),
            map(() => ProcessActions.complianceReportWaitEnded({ processId })),
          ),
        ).pipe(takeUntil(this.cancelProcessScopedRequests$));
      }),
    )
  );

  /** A save of the officer's analysis on a COMPLETED process outdates its full report: the backend generates it again
   *  (US-002), and the page says so until the new one is there. A late answer for a process no longer open is not
   *  this page's (the measures' answer names none: they are the open process's). */
  readonly complianceReportOutdatedOnAnalysisSave$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        ProcessActions.saveObservationSuccess,
        ProcessActions.saveRiskResultsSuccess,
        ProcessActions.saveRiskMeasuresSuccess,
      ),
      withLatestFrom(
        this.store.select(selectSelectedProcess),
        this.store.select(selectHasPermission(Permission.COMPLIANCE_REPORT_READ)),
      ),
      filter(([save, process, canRead]) => canRead && process?.status === 'COMPLETED' && !!process.id
        && (!('processId' in save) || save.processId === process.id)),
      map(([, process]) => ProcessActions.complianceReportOutdated({ processId: process!.id! })),
    )
  );

  /** Signature status only matters once a report exists for the active process.
   *  This avoids carrying over stale signature UI for completed processes that
   *  do not yet have a generated report. */
  readonly loadSignatureStatusOnReportReady$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProcessActions.loadKycReportSuccess),
      filter(({ report }) => !!report.processId),
      map(({ report }) => ProcessActions.loadSignatureStatus({ processId: report.processId! })),
    )
  );

  // Entrust signature
  readonly loadSignatureStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProcessActions.loadSignatureStatus),
      switchMap(({ processId }) =>
        getSignatureStatus(this.http, this.apiConfig.rootUrl, { processId }).pipe(
          takeUntil(this.cancelProcessScopedRequests$),
          switchMap((response) =>
            parseResponseBody<EntrustSignatureStatusResponseDto>(response.body).pipe(
              map((status) =>
                status
                  ? ProcessActions.loadSignatureStatusSuccess({ status })
                  : ProcessActions.loadSignatureStatusEmpty()
              )
            )
          ),
          catchError((error) => {
            const status = (error as { status?: number } | null)?.status;
            if (status === 404) {
              return of(ProcessActions.loadSignatureStatusEmpty());
            }
            return failWith('PROCESS.ERROR.LOAD_SIGNATURE_STATUS', (message) => ProcessActions.loadSignatureStatusFailure({ error: message }))(error);
          })
        )
      )
    )
  );

  readonly requestSignature$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProcessActions.requestSignature),
      switchMap(({ processId, body, silent }) =>
        requestSignatureFn(this.http, this.apiConfig.rootUrl, { processId, body }).pipe(
          switchMap((response) =>
            parseResponseBody<EntrustSignatureStartResponseDto>(response.body).pipe(
              switchMap((createdResponse) => [
                ProcessActions.requestSignatureSuccess({ processId, response: createdResponse, mode: body.mode }),
                ProcessActions.loadSignatureStatus({ processId }),
                ProcessActions.loadTimeline({ processId }),
                ToastActions.showToast({
                  message: 'PROCESS.SIGNATURE.REQUEST_SUCCESS',
                  notificationType: 'success',
                }),
              ])
            )
          ),
          catchError(failWith('PROCESS.ERROR.REQUEST_SIGNATURE', (error) => ProcessActions.requestSignatureFailure({
              // 400/409 messages come straight from the backend and are meant
              // to be shown as-is (missing prerequisites, compliance not
              // approved yet, request already in progress).
              error,
              processId,
              silent,
            })))
        )
      )
    )
  );

  // Client consent evidence (US-015)
  /** None on file is a 404: no evidence, not a failure. A 403 (a role the backend does not show it to) is unknown. */
  readonly loadConsentEvidence$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProcessActions.loadConsentEvidence),
      switchMap(({ processId }) =>
        getConsentEvidence(this.http, this.apiConfig.rootUrl, { processId }).pipe(
          map((response) => ProcessActions.loadConsentEvidenceSuccess({ processId, evidence: response.body ?? null })),
          catchError((error: unknown) => {
            const status = error instanceof HttpErrorResponse ? error.status : undefined;
            if (status === 404) return of(ProcessActions.loadConsentEvidenceSuccess({ processId, evidence: null }));
            if (status === 403) return of(ProcessActions.loadConsentEvidenceHidden({ processId }));
            return failWith('PROCESS.ERROR.LOAD_CONSENT_EVIDENCE',
              (message) => ProcessActions.loadConsentEvidenceFailure({ processId, error: message }))(error);
          })
        )
      )
    )
  );

  /** A write: a second upload for the same process is ignored while the first runs. Its failure is shown at the file
   *  (no toast). */
  readonly uploadConsentEvidence$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProcessActions.uploadConsentEvidence),
      groupBy(({ processId }) => processId),
      mergeMap((byProcess) => byProcess.pipe(exhaustMap(({ processId, file }) =>
        uploadConsentEvidence(this.http, this.apiConfig.rootUrl, { processId, body: { file } }).pipe(
          switchMap((response) => [
            ProcessActions.uploadConsentEvidenceSuccess({ processId, evidence: response.body as KycConsentEvidenceDto }),
            ToastActions.showToast({ message: 'PROCESS.CONSENT_EVIDENCE.UPLOAD_SUCCESS', notificationType: 'success' }),
          ]),
          catchError(failWith('PROCESS.ERROR.UPLOAD_CONSENT_EVIDENCE', (error, apiError) =>
            ProcessActions.uploadConsentEvidenceFailure({ processId, error, code: apiError.code })))
        )
      )))
    )
  );

  /** Refused as locked: the process moved on elsewhere (signed, skipped, reopened), so its status and signature are
   *  read again and the page offers what is still possible. Only for the process still open. */
  readonly refreshAfterLockedConsentEvidence$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProcessActions.uploadConsentEvidenceFailure),
      filter(({ code }) => code === CONSENT_EVIDENCE_LOCKED),
      withLatestFrom(this.store.select(selectSelectedProcess)),
      filter(([{ processId }, selected]) => selected?.id === processId),
      switchMap(([{ processId }]) => [
        ProcessActions.refreshProcessStatus({ processId }),
        ProcessActions.loadSignatureStatus({ processId }),
        ProcessActions.loadKycReport({ processId }),
      ])
    )
  );

  /** Completes the process without collecting signatures. Reloads the report
   *  (signatureSkipped flag) and timeline (SIGNATURE_SKIPPED event) so both
   *  reflect the skip without a manual refresh. */
  readonly skipSignature$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProcessActions.skipSignature),
      switchMap(({ processId }) =>
        skipSignatureFn(this.http, this.apiConfig.rootUrl, { processId }).pipe(
          switchMap((response) =>
            parseResponseBody<EntrustSignatureStatusResponseDto>(response.body).pipe(
              switchMap((status) => [
                ProcessActions.skipSignatureSuccess({ processId, status: status ?? { status: 'SKIPPED' } }),
                ProcessActions.loadKycReport({ processId }),
                ProcessActions.loadTimeline({ processId }),
                ToastActions.showToast({
                  message: 'PROCESS.SIGNATURE.SKIP_SUCCESS',
                  notificationType: 'success',
                }),
              ])
            )
          ),
          catchError(failWith('PROCESS.ERROR.SKIP_SIGNATURE', (error) => ProcessActions.skipSignatureFailure({
              // 409 messages come straight from the backend (not completed,
              // compliance not approved, request in progress, already signed
              // or already skipped) and are meant to be shown as-is.
              error,
            })))
        )
      )
    )
  );

  /** Long-poll the signature status endpoint while an existing request is
   *  non-terminal, so the status tag reflects webhook-driven updates without
   *  a manual reload. Started/stopped from process-detail.component.ts. */
  readonly pollSignatureStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProcessActions.startSignaturePolling),
      switchMap(({ processId }) =>
        timer(SIGNATURE_POLL_INTERVAL_MS, SIGNATURE_POLL_INTERVAL_MS).pipe(
          takeUntil(this.actions$.pipe(ofType(ProcessActions.stopSignaturePolling))),
          map(() => ProcessActions.loadSignatureStatus({ processId })),
        ),
      ),
    )
  );

  /** The backend writes a SIGNATURE_COMPLETED timeline event when a signer
   *  run resolves (webhook or reconciliation), so reload the timeline
   *  whenever a polled status moves on - either leg in CLIENT_AND_BOARD_MEMBER
   *  mode - for the same process. */
  readonly reloadTimelineOnSignatureProgress$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProcessActions.loadSignatureStatusSuccess),
      map(({ status }) => ({
        processId: status.kycProcessId,
        // The runs too: a new attempt can end in the same statuses as the previous one.
        key: [status.currentSigner, status.workflowRunId, status.status,
          status.boardMember?.workflowRunId, status.boardMember?.status, status.boardMember?.clientHandOff]
          .map((part) => part ?? '').join('|'),
      })),
      pairwise(),
      filter(([prev, curr]) => !!curr.processId && prev.processId === curr.processId && prev.key !== curr.key),
      map(([, curr]) => ProcessActions.loadTimeline({ processId: curr.processId! })),
    )
  );

  /** Signature evidence folders (Report Evidence section). The backend
   *  restricts this endpoint to ADMINISTRATION/COMPLIANCE_OFFICER, so unlike
   *  the report/signature status this is loaded on demand by the
   *  report-evidence component (only rendered for those roles) rather than
   *  eagerly for every viewer of a completed process. */
  readonly loadEvidenceFolders$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProcessActions.loadEvidenceFolders),
      switchMap(({ processId }) =>
        getEvidenceFolders(this.http, this.apiConfig.rootUrl, { processId }).pipe(
          takeUntil(this.cancelProcessScopedRequests$),
          switchMap((response) =>
            parseResponseBody<EntrustSignatureEvidenceFolderDto[]>(response.body).pipe(
              map((evidenceFolders) =>
                ProcessActions.loadEvidenceFoldersSuccess({ evidenceFolders: evidenceFolders ?? [] })
              )
            )
          ),
          catchError(failWith('PROCESS.ERROR.LOAD_EVIDENCE_FOLDERS', (error) => ProcessActions.loadEvidenceFoldersFailure({
              error,
            })))
        )
      )
    )
  );

  readonly loadProcessStatistics$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProcessActions.loadProcessStatistics),
      switchMap(({ managementCompanyId, filters }) =>
        getStatistics(this.http, this.apiConfig.rootUrl, { managementCompanyId, ...filters }).pipe(
          switchMap((response) =>
            parseResponseBody<KycProcessStatisticsDto>(response.body).pipe(
              map((statistics) => ProcessActions.loadProcessStatisticsSuccess({ statistics: statistics ?? {} })),
            ),
          ),
          catchError(failWith('PROCESS.ERROR.LOAD_STATISTICS', (error) => ProcessActions.loadProcessStatisticsFailure({ error }))),
        ),
      ),
    ),
  );

  readonly handleFailures$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        ProcessActions.loadProcessesFailure,
        ProcessActions.loadProcessFailure,
        ProcessActions.loadIndividualFormFailure,
        ProcessActions.saveIndividualFormFailure,
        ProcessActions.loadCompanyFormFailure,
        ProcessActions.saveCompanyFormFailure,
        ProcessActions.loadChangeRequestsFailure,
        ProcessActions.createChangeRequestFailure,
        ProcessActions.closeChangeRequestFailure,
        ProcessActions.reopenChangeRequestFailure,
        ProcessActions.transitionStatusFailure,
        ProcessActions.reopenProcessFailure,
        ProcessActions.clientReopenProcessFailure,
        ProcessActions.loadFormVersionsFailure,
        ProcessActions.loadFormVersionDetailFailure,
        ProcessActions.submitProcessFailure,
        ProcessActions.loadApprovalStateFailure,
        ProcessActions.loadApprovalsFailure,
        ProcessActions.decideApprovalFailure,
        ProcessActions.loadTimelineFailure,
        ProcessActions.loadParticipantsFailure,
        ProcessActions.loadRiskMatrixStateFailure,
        ProcessActions.assessRiskMatrixFailure,
        ProcessActions.loadRiskMeasuresFailure,
        ProcessActions.saveRiskMeasuresFailure,
        ProcessActions.saveRiskResultsFailure,
        ProcessActions.loadObservationFailure,
        ProcessActions.saveObservationFailure,
        ProcessActions.loadScreeningsFailure,
        ProcessActions.runScreeningsFailure,
        ProcessActions.loadWorldcheckCasesFailure,
        ProcessActions.loadKycReportFailure,
        ProcessActions.loadSignatureStatusFailure,
        ProcessActions.requestSignatureFailure,
        ProcessActions.skipSignatureFailure,
        ProcessActions.loadEvidenceFoldersFailure,
      ),
      // Background-initiated failures (currently only the Entrust status
      // refresh's `loadCompanyForm({ silent: true })`) skip the generic
      // intrusive toast — every other caller omits `silent` and is unaffected.
      filter((action) => !('silent' in action && action.silent)),
      // A form save conflict is shown as the reload dialog instead (Form Save Conflict, US-024).
      filter((action) => !('code' in action && !!action.code && FORM_CONFLICT_CODES.includes(action.code))),
      // The results form shows a reassessment date before the assessment at the date itself (US-011).
      filter((action) => !(action.type === ProcessActions.saveRiskResultsFailure.type
        && action.code === RISK_RESULTS_DATE_BEFORE_ASSESSMENT)),
      map(({ error }) =>
        ToastActions.showToast({
          message: error,
          notificationType: 'error',
        })
      )
    )
  );
}
